import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { X, ArrowLeft, Calendar, Clock, User, Phone, Mail } from 'lucide-react';
import { timeSlots } from '../data/mock';
import { supabase } from '../lib/supabase';
import '../styles/booking.css';
import '../styles/doctor-booking-flow.css';

// Import CustomSelect and CustomDatePicker from BookingModal
import { CustomSelect, CustomDatePicker } from './BookingModalComponents';

const BookingFormModal = React.memo(({ doctor, service, onBack, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: null,
    time: ''
  });
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState(timeSlots);
  const [errors, setErrors] = useState({});

  // Load blocked slots when date changes
  useEffect(() => {
    if (formData.date) {
      loadBlockedSlots();
    } else {
      setAvailableTimeSlots(timeSlots);
    }
  }, [formData.date, doctor, loadBlockedSlots]);

  // Ініціалізація при першому завантаженні
  useEffect(() => {
    if (formData.date) {
      loadBlockedSlots();
    }
  }, []);

  const loadBlockedSlots = useCallback(async () => {
    try {
      // Використовуємо локальну дату
      const year = formData.date.getFullYear();
      const month = String(formData.date.getMonth() + 1).padStart(2, '0');
      const day = String(formData.date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const doctorName = `${doctor.name} (${doctor.position})`;
      
      // Завантажити заблоковані слоти
      const { data: blockedData, error: blockedError } = await supabase
        .from('blocked_slots')
        .select('blocked_time')
        .eq('doctor_name', doctorName)
        .eq('blocked_date', dateStr);

      if (blockedError) throw blockedError;
      
      // Завантажити записи (зайняті часи)
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('booking_time')
        .eq('doctor_name', doctorName)
        .eq('booking_date', dateStr)
        .neq('status', 'cancelled');

      if (bookingsError) throw bookingsError;
      
      // Об'єднати заблоковані та зайняті часи
      const blockedTimes = blockedData?.map(slot => slot.blocked_time) || [];
      const bookedTimes = bookingsData?.map(booking => booking.booking_time) || [];
      const allUnavailable = [...new Set([...blockedTimes, ...bookedTimes])];
      
      setBlockedSlots(allUnavailable);
      
      // Фільтрувати доступні часи
      const available = timeSlots.filter(time => !allUnavailable.includes(time));
      
      setAvailableTimeSlots(available);
      
      // Скинути час якщо він тепер зайнятий
      if (formData.time && allUnavailable.includes(formData.time)) {
        setFormData(prev => ({ ...prev, time: '' }));
      }
    } catch (error) {
      console.error('Error loading blocked slots:', error);
    }
  }, [formData.date, formData.time, doctor]);

  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Очистити помилку для цього поля
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  }, [errors]);

  const handleSelectChange = useCallback((field) => (value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Очистити помилку для цього поля
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  }, [errors]);

  const handleDateChange = useCallback((date) => {
    setFormData(prev => ({
      ...prev,
      date: date,
      time: '' // Сбрасываем время при смене даты
    }));
    // Очистити помилку для дати
    if (errors.date) {
      setErrors(prev => ({
        ...prev,
        date: false
      }));
    }
    // Очистити помилку для часу, якщо була
    if (errors.time) {
      setErrors(prev => ({
        ...prev,
        time: false
      }));
    }
  }, [errors]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    // Валідація всіх полів
    const newErrors = {};
    if (!formData.name) newErrors.name = true;
    if (!formData.phone) newErrors.phone = true;
    if (!formData.date) newErrors.date = true;
    if (!formData.time) newErrors.time = true;
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit(formData);
  }, [formData, onSubmit]);

  const timeOptions = useMemo(() => availableTimeSlots.map(time => ({
    value: time,
    label: time
  })), [availableTimeSlots]);

  const isTimeDisabled = !formData.date;
  const timePlaceholder = isTimeDisabled ? 'Спочатку оберіть дату' : 'Оберіть час';

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-form-title"
    >
      <div className="modal-content booking-form-modal" onClick={(e) => e.stopPropagation()}>
        <button 
          className="modal-back" 
          onClick={onBack} 
          type="button"
          aria-label="Повернутися до вибору послуги"
        >
          <ArrowLeft size={24} aria-hidden="true" />
        </button>
        
        <button 
          className="modal-close" 
          onClick={onClose} 
          type="button"
          aria-label="Закрити вікно"
        >
          <X size={24} aria-hidden="true" />
        </button>

        <h2 id="booking-form-title" className="modal-title">Запис на прийом</h2>
        <p className="modal-subtitle">Заповніть форму для запису</p>

        <form className="booking-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>
                <User size={18} />
                Лікар
              </label>
              <input
                type="text"
                value={`Др. ${doctor.name}`}
                disabled
                className="pre-filled-field"
                aria-label={`Обраний лікар: Др. ${doctor.name}`}
              />
            </div>

            <div className="form-group">
              <label>
                <Mail size={18} />
                Послуга
              </label>
              <input
                type="text"
                value={service.title}
                disabled
                className="pre-filled-field"
                aria-label={`Обрана послуга: ${service.title}`}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <User size={18} />
                Ім'я *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ваше ім'я"
                className={errors.name ? 'error' : ''}
                required
              />
            </div>

            <div className="form-group">
              <label>
                <Phone size={18} />
                Телефон *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+380"
                className={errors.phone ? 'error' : ''}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>
              <Mail size={18} />
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <Calendar size={18} />
                Дата *
              </label>
              <div className="date-picker-container">
                <CustomDatePicker 
                  selectedDate={formData.date} 
                  onChange={handleDateChange}
                  error={errors.date}
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <Clock size={18} />
                Час *
              </label>
              <CustomSelect
                options={timeOptions}
                value={formData.time}
                onChange={handleSelectChange('time')}
                placeholder={timePlaceholder}
                icon={<Clock size={18} />}
                error={errors.time}
                disabled={isTimeDisabled}
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            aria-label="Підтвердити запис на прийом"
          >
            <Calendar size={20} aria-hidden="true" />
            Підтвердити запис
          </button>
        </form>
      </div>
    </div>
  );
});

BookingFormModal.displayName = 'BookingFormModal';

export default BookingFormModal;
